import { FC, useCallback, useEffect, useRef } from 'react'

import {
  useOnSelectionChange,
  OnSelectionChangeFunc,
  useReactFlow
} from '@xyflow/react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  GreyGrey30,
  GreyWhite,
  ShadowL,
  Flex
} from '@sequencehq/core-components'
import { useNode } from 'Automations/nodeDefinitions'
import {
  configFormExpandInteractedAtom,
  historyNavigationCountAtom,
  isConfigFormExpandedAtom,
  isEditingDisabledAtom,
  selectedEdgesAtom,
  selectedNodeIdsAtom,
  showAddNodeToolbarAtom
} from 'Automations/automationEditor/atoms'
import { useConfigFormExpandHeightTransition } from 'Automations/automationEditor/hooks'

const CONFIG_PANEL_SELECTOR =
  '[data-testid^="automations.editor.configEditor."]'
const CANVAS_SELECTOR = '[data-testid="automations.editor.canvas"]'
const TOOLBAR_SELECTOR = '[data-testid="automations.editor.addNodeToolbar"]'

export const ConfigEditor: FC = () => {
  const [selectedNodeIds, setSelectedNodeIds] = useAtom(selectedNodeIdsAtom)
  const setSelectedEdges = useSetAtom(selectedEdgesAtom)
  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)
  const setExpanded = useSetAtom(isConfigFormExpandedAtom)
  const setHasInteracted = useSetAtom(configFormExpandInteractedAtom)

  const onSelectionChange: OnSelectionChangeFunc = useCallback(
    ({ nodes, edges }) => {
      setSelectedNodeIds(prev => {
        const newIds = nodes.map(node => node.id)
        if (
          prev.length === newIds.length &&
          prev.every((id, i) => id === newIds[i])
        ) {
          return prev
        }
        return newIds
      })
      setSelectedEdges(edges)
    },
    [setSelectedNodeIds, setSelectedEdges]
  )

  useOnSelectionChange({ onChange: onSelectionChange })

  const firstNodeId = selectedNodeIds.at(0)
  const singleSelectedNodeId =
    selectedNodeIds.length === 1 ? firstNodeId : undefined

  // Reset expansion (and the interaction flag that gates its animation)
  // whenever the selected node changes — including when the config form closes
  // (singleSelectedNodeId becomes undefined). Resetting on close, rather than
  // only on the next node's mount, ensures the expanded state never leaks into
  // a reopen: a stale expanded panel would be measured by usePanToSelectedNode
  // and cause the canvas to pan as if avoiding the larger expanded panel.
  useEffect(() => {
    setExpanded(false)
    setHasInteracted(false)
  }, [singleSelectedNodeId, setExpanded, setHasInteracted])

  usePanToSelectedNode(singleSelectedNodeId)

  if (isEditingDisabled) return null
  if (!firstNodeId || selectedNodeIds.length !== 1) return null

  return <ConfigEditorBody selectedNodeId={firstNodeId} />
}

/**
 * Pans the canvas only when the selected node overlaps the config panel.
 * When panning is needed, positions the node so its top-right corner sits
 * 32px from the panel's bottom-left corner.
 *
 * That placement can push a tall node (e.g. an expanded node) off the bottom
 * of the canvas, behind the floating toolbar. So the pan is then clamped to
 * keep the whole node within the visible canvas, lifting it up until its bottom
 * clears the toolbar (without pushing its top out of view).
 *
 * Uses a ref to track the last panned node rather than usePrevious,
 * because re-renders triggered by setNodes would cancel a
 * requestAnimationFrame via effect cleanup before it could fire.
 */
const NODE_TO_PANEL_GAP = 32

// Margin kept between the node and the viewport edges (and the floating
// toolbar) when the pan is clamped to keep the whole node on screen.
const NODE_TO_VIEWPORT_MARGIN = 16

const usePanToSelectedNode = (nodeId: string | undefined) => {
  const reactFlow = useReactFlow()
  const lastPannedNodeIdRef = useRef<string | undefined>()

  // Store the ReactFlow instance in a ref so the effect only depends on
  // nodeId — avoids re-runs (and potential rAF cancellation) when
  // function references change between renders.
  const reactFlowRef = useRef(reactFlow)
  reactFlowRef.current = reactFlow

  useEffect(() => {
    if (!nodeId) {
      lastPannedNodeIdRef.current = undefined
      return
    }

    if (nodeId === lastPannedNodeIdRef.current) return
    lastPannedNodeIdRef.current = nodeId

    const { getNode, flowToScreenPosition, getViewport, getZoom, setCenter } =
      reactFlowRef.current
    const node = getNode(nodeId)
    if (!node) return

    // Defer measurement to the next animation frame so the config panel
    // has been painted and can be measured via getBoundingClientRect.
    requestAnimationFrame(() => {
      const canvasEl = document.querySelector(CANVAS_SELECTOR)
      if (!canvasEl) return

      // If the config panel hasn't rendered (e.g. the node has no
      // configForm), skip panning — there's nothing to avoid.
      const configPanelEl = canvasEl.querySelector(CONFIG_PANEL_SELECTOR)
      if (!configPanelEl) return

      const nodeWidth = node.measured?.width ?? 200
      const nodeHeight = node.measured?.height ?? 100

      // Convert the node's bounding corners to screen coordinates so we
      // can check for intersection with the config panel.
      const nodeTopRightScreen = flowToScreenPosition({
        x: node.position.x + nodeWidth,
        y: node.position.y
      })
      const nodeBottomLeftScreen = flowToScreenPosition({
        x: node.position.x,
        y: node.position.y + nodeHeight
      })

      const nodeLeft = nodeBottomLeftScreen.x
      const nodeRight = nodeTopRightScreen.x
      const nodeTop = nodeTopRightScreen.y
      const nodeBottom = nodeBottomLeftScreen.y

      const panelRect = configPanelEl.getBoundingClientRect()

      // Check whether the node rectangle (expanded by NODE_TO_PANEL_GAP)
      // intersects the config panel rectangle.
      const intersectsX =
        nodeRight > panelRect.left - NODE_TO_PANEL_GAP &&
        nodeLeft < panelRect.right
      const intersectsY =
        nodeTop < panelRect.bottom + NODE_TO_PANEL_GAP &&
        nodeBottom > panelRect.top
      if (!intersectsX || !intersectsY) return

      // Ideal screen-space shift: drop the node's top-right corner to the
      // panel's bottom-left corner, offset by the gap. idealDx is negative
      // (move the node left, clear of the panel); idealDy is positive (move it
      // down, below the panel).
      const idealDx = panelRect.left - NODE_TO_PANEL_GAP - nodeRight
      const idealDy = panelRect.bottom + NODE_TO_PANEL_GAP - nodeTop

      // The visible region the node must stay within. The floating toolbar
      // sits over the bottom of the canvas, so reserve its band — falling back
      // to the canvas edge when the toolbar isn't shown.
      const canvasRect = canvasEl.getBoundingClientRect()
      const toolbarTop =
        canvasEl.querySelector(TOOLBAR_SELECTOR)?.getBoundingClientRect().top ??
        canvasRect.bottom
      const visibleTop = canvasRect.top + NODE_TO_VIEWPORT_MARGIN
      const visibleBottom = toolbarTop - NODE_TO_VIEWPORT_MARGIN

      // The ideal shift can push a tall node past the visible bottom (behind
      // the toolbar). Lift it back up so its bottom clears the visible region,
      // but never so far that its top leaves the visible region.
      const bottomOverflow = nodeBottom + idealDy - visibleBottom
      const liftHeadroom = nodeTop + idealDy - visibleTop
      const lift = Math.max(0, Math.min(bottomOverflow, liftHeadroom))

      // Shift the current viewport centre (in flow units) so the node moves by
      // exactly (idealDx, idealDy - lift) on screen.
      const zoom = getZoom()
      const { x: vx, y: vy } = getViewport()

      const currentCenterFlowX = (canvasRect.width / 2 - vx) / zoom
      const currentCenterFlowY = (canvasRect.height / 2 - vy) / zoom

      setCenter(
        currentCenterFlowX - idealDx / zoom,
        currentCenterFlowY - (idealDy - lift) / zoom,
        { zoom, duration: 300 }
      )
    })
  }, [nodeId])
}

/**
 * Adjustable gap (px) between the bottom of the expanded config panel and the top of the floating AddNodeToolbar
 */
const CONFIG_PANEL_TO_TOOLBAR_GAP = 16

const ConfigEditorBody: FC<{ selectedNodeId: string }> = ({
  selectedNodeId
}) => {
  const { definition } = useNode({ nodeId: selectedNodeId })
  const isExpanded = useAtomValue(isConfigFormExpandedAtom)
  const showToolbar = useAtomValue(showAddNodeToolbarAtom)
  const historyNavigationCount = useAtomValue(historyNavigationCountAtom)
  const panelRef = useRef<HTMLDivElement>(null)

  useConfigFormExpandHeightTransition(panelRef, `${isExpanded}:${showToolbar}`)

  if (!definition) return null

  const ConfigForm = definition.configForm
  if (!ConfigForm) return null

  const ADD_NODE_TOOLBAR_HEIGHT: number = 50
  const ADD_NODE_TOOLBAR_BOTTOM: number = 12

  const verticalProps = isExpanded
    ? {
        bottom: showToolbar
          ? `${ADD_NODE_TOOLBAR_BOTTOM + ADD_NODE_TOOLBAR_HEIGHT + CONFIG_PANEL_TO_TOOLBAR_GAP}px`
          : `${ADD_NODE_TOOLBAR_BOTTOM}px`
      }
    : { maxHeight: 'calc(100% - 67px)' }

  return (
    <Flex
      ref={panelRef}
      data-testid={`automations.editor.configEditor.${selectedNodeId}`}
      background={GreyWhite}
      position="absolute"
      top="12px"
      right="12px"
      border={`${GreyGrey30} 1px solid`}
      boxShadow={ShadowL}
      borderRadius="12px"
      flexDir="column"
      gap="8px"
      overflowY="auto"
      {...verticalProps}
    >
      {ConfigForm && (
        <ConfigForm
          // Also keyed by the history navigation count so undo/redo remounts
          // the form — its editors hold local state (e.g. tiptap documents)
          // that would otherwise keep displaying the undone values.
          key={`${selectedNodeId}:${historyNavigationCount}`}
          nodeId={selectedNodeId}
        />
      )}
    </Flex>
  )
}
